import { Link } from "react-router-dom";
import { Suspense } from "react";
import Navigation from "@/components/Navigation";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/Footer";
import HeroSection from "@/components/layout/HeroSection";
import SEOHelmet from "@/components/SEOHelmet";
import StructuredData from "@/components/StructuredData";
import { CoverageCards, ExpertSection, CyberThreatsCards, CTASection } from "@/utils/lazyComponents";
import NewsletterCTA from "@/components/NewsletterCTA";
import { Shield, Lock, Activity, CheckCircle2, ArrowRight, Phone, FileSearch, Mail, Stethoscope, Database, AlertTriangle, Download } from "lucide-react";
import { PrefetchLink } from "@/components/PrefetchLink";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const Home = () => {
  // Main benefits (horizontal, no cards)
  const mainBenefits = [{
    icon: Shield,
    title: "Eigenschaden",
    desc: "IT-Schäden & Betriebsunterbrechung",
    link: "/cyberversicherung/betriebsunterbrechung"
  }, {
    icon: Lock,
    title: "Haftpflicht",
    desc: "DSGVO & Drittschäden",
    link: "/cyberversicherung/dsgvo-bussgelder"
  }, {
    icon: Activity,
    title: "24/7 Notfall",
    desc: "Sofortige IT-Hilfe",
    link: "/cyberrisiken"
  }];

  // Services (2-column list)
  const services = [{
    title: "IT-Forensik & Wiederherstellung",
    items: ["Datenrettung und Systemwiederherstellung", "Forensische Analyse des Cyberangriffs", "Professionelle IT-Notfallhilfe 24/7"]
  }, {
    title: "Betriebsunterbrechung",
    items: ["Fortlaufende Kosten während Systemausfall", "Umsatzausfall durch IT-Stillstand", "Wiederanlaufkosten"]
  }, {
    title: "DSGVO & Rechtsschutz",
    items: ["Bußgelder und Sanktionen", "Anwaltskosten und Verfahren", "Benachrichtigungspflichten erfüllen"]
  }, {
    title: "Krisenmanagement",
    items: ["PR-Beratung bei Reputationsschäden", "Externe Datenschutzbeauftragte", "Telefonhotline für betroffene Patienten"]
  }];

  // Practice types (minimal)
  const practiceTypes = [{
    title: "Hausarztpraxen",
    icon: Activity,
    link: "/cyberversicherung/hausarztpraxis"
  }, {
    title: "Fachärzte",
    icon: Shield,
    link: "/cyberversicherung/facharztpraxis"
  }, {
    title: "Zahnärzte",
    icon: Stethoscope,
    link: "/cyberversicherung/zahnarztpraxis"
  }, {
    title: "MVZ",
    icon: Database,
    link: "/cyberversicherung/mvz"
  }, {
    title: "Kliniken",
    icon: AlertTriangle,
    link: "/cyberversicherung/klinik"
  }];

  // Timeline steps
  const steps = [{
    number: 1,
    icon: Phone,
    title: "Kostenlose Beratung",
    description: "Wir analysieren Ihre individuellen Cyber-Risiken"
  }, {
    number: 2,
    icon: FileSearch,
    title: "Tarifvergleich",
    description: "Wir finden die beste Police für Ihre Praxis"
  }, {
    number: 3,
    icon: CheckCircle2,
    title: "Rundum geschützt",
    description: "24/7 abgesichert gegen alle Cyber-Bedrohungen"
  }];

  // FAQ (Top 3)
  const faqs = [{
    question: "Was kostet eine Cyberversicherung für Arztpraxen?",
    answer: "Die Kosten variieren je nach Praxisgröße, IT-Infrastruktur und gewünschter Deckungssumme. Kleine Praxen zahlen ab ca. 500€/Jahr, größere Einrichtungen 1.500-3.000€."
  }, {
    question: "Welche Schäden sind versichert?",
    answer: "Die Versicherung deckt IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung, DSGVO-Bußgelder, Rechtskosten und Krisenmanagement ab."
  }, {
    question: "Greift die Versicherung auch bei Mitarbeiter-Fehlern?",
    answer: "Ja, die meisten Tarife decken auch Schäden durch unbeabsichtigte Fehler von Mitarbeitern ab, z.B. das Öffnen einer Phishing-E-Mail."
  }];
  return <div className="min-h-screen relative">
      <SEOHelmet title="Cyberversicherung für Arztpraxen | Praxis Versichert" description="Umfassender Cyber-Schutz für medizinische Einrichtungen. DSGVO-konform, 24/7 IT-Notfallhilfe & Schutz vor Ransomware. Jetzt kostenlos beraten lassen!" canonical="https://praxis-versichert.de/" ogImage="https://praxis-versichert.de/assets/hero-medical-cyber-main.webp" ogType="website" />
      
      <StructuredData data={[{
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Praxis Versichert",
      "url": "https://praxis-versichert.de",
      "logo": "https://praxis-versichert.de/logo.png",
      "description": "Spezialisiert auf Cyberversicherungen für medizinische Einrichtungen",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Kundenservice",
        "email": "kontakt@praxis-versichert.de",
        "availableLanguage": "de"
      },
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "DE"
      }
    }, {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Praxis Versichert",
      "url": "https://praxis-versichert.de",
      "description": "Cyberversicherung für Arztpraxen und medizinische Einrichtungen",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://praxis-versichert.de/?s={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }, {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Cyberversicherung für Arztpraxen",
      "provider": {
        "@type": "Organization",
        "name": "Praxis Versichert"
      },
      "areaServed": "DE",
      "description": "Umfassender Cyber-Schutz für medizinische Einrichtungen mit DSGVO-Konformität und 24/7 IT-Notfallhilfe"
    }, {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home"
      }]
    }]} />
      
      
      <div className="relative z-10">
        <Navigation />
        <CookieBanner />
        
        <main>
        {/* Hero Section */}
        <HeroSection image="/assets/hero-medical-cyber-main.webp" alt="Cyberversicherung für Arztpraxen 2025 - Umfassender Schutz vor Cyberangriffen" title="Umfassender Cyber-Schutz für Arztpraxen – DSGVO-konform mit 24/7 IT-Notfallhilfe" gradientPreset="none" imageOpacity={40} overlayColor="background" className="py-12 sm:py-16 lg:py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
              <Stethoscope className="w-7 h-7 text-accent" />
            </div>
            
            <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 text-foreground leading-tight px-2 xs:px-4 sm:px-0">Cyberversicherung<br />für Arztpraxen</h1>
            
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-xs xs:text-sm md:text-base font-medium">
                Umfassender Schutz • Schnelle Hilfe • Deutschlandweit
              </span>
            </div>
            
            <div className="bg-destructive/10 backdrop-blur-sm rounded-lg p-4 max-w-2xl mx-auto mb-8 border border-destructive/30">
              <p className="text-xs xs:text-sm md:text-base text-destructive flex items-center justify-center gap-2 font-semibold">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <span><strong>58%</strong> aller Praxen wurden bereits angegriffen</span>
                <span className="mx-2">•</span>
                <span>Ø Schaden: <strong>68.451€</strong></span>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/termin-buchen" className="inline-flex items-center justify-center gap-2 h-11 px-8 bg-transparent text-white border-2 border-accent rounded-md font-medium text-sm transition-all hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80 group">
                Jetzt beraten lassen
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform hidden sm:inline-block" />
              </Link>
              <Button size="lg" variant="outline" asChild className="w-full sm:w-auto">
                <a href="https://kunden-portal.cyberdirekt.de/?ref=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3NTA3YmQwMTlmM2IyYjU0OGM5NGEzYyIsImZpcnN0bmFtZSI6IlRob21hcyIsImxhc3RuYW1lIjoiRGV3ZWluIn0.X_v1hAubU2FypQ_89Nk8_9NFRhIrRDBv0Cmv4egtB30%2F#/versicherung/1a/ihr-unternehmen/branche" target="_blank" rel="noopener noreferrer">
                  Vergleichsrechner starten
                </a>
              </Button>
            </div>

            <NewsletterCTA variant="hero" compact={true} />
          </div>
        </HeroSection>

      {/* Keyword-Rich Content Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <p className="text-sm sm:text-base text-foreground/80 text-center leading-relaxed">
            Eine <PrefetchLink to="/cyberversicherung" className="text-accent hover:underline font-semibold">Cyberversicherung für Arztpraxen</PrefetchLink> schützt Sie umfassend vor den finanziellen Folgen von Cyberangriffen. 
            Ob <PrefetchLink to="/cyberversicherung/ransomware-schutz" className="text-accent hover:underline">Ransomware-Attacke</PrefetchLink>, <PrefetchLink to="/cyberversicherung/datenverlust" className="text-accent hover:underline">Datenleck</PrefetchLink> oder <PrefetchLink to="/cyberversicherung/dsgvo-bussgelder" className="text-accent hover:underline">DSGVO-Verstoß</PrefetchLink> – unsere spezialisierten <PrefetchLink to="/tarifwissen" className="text-accent hover:underline font-semibold">Cyberversicherung Tarife</PrefetchLink> bieten Ihnen <strong>professionellen Schutz</strong> für Ihre medizinische Einrichtung. Vergleichen Sie jetzt die <strong>besten Anbieter</strong> und finden Sie die optimale <strong>Cyberversicherung</strong> für Ihre Praxis.
          </p>
        </div>
      </section>

      {/* Cyberbedrohungen Section - Neues Card-Layout */}
      <Suspense fallback={<div className="h-96 bg-muted/5 rounded-lg animate-pulse" />}>
        <CyberThreatsCards />
      </Suspense>

      {/* Checklisten CTA */}
      <section className="py-12 px-4 bg-neutral-900">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-card/90 backdrop-blur-sm border-2 border-accent/30 rounded-lg p-6 sm:p-8 hover:border-accent/50 hover:shadow-[0_0_30px_hsl(var(--accent)/0.2)] transition-all duration-300">
            
            {/* Icon + Headline */}
            <div className="flex flex-col items-center justify-center gap-3 mb-4">
              <Download className="w-8 h-8 sm:w-10 sm:h-10 text-accent" />
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-white">
            Kostenlose<br />Cyber-Checkliste für Arztpraxen
          </h2>
            </div>

            {/* Beschreibung */}
            <p className="text-sm sm:text-base text-center text-foreground/80 mb-6 max-w-2xl mx-auto">
              15 kritische Sicherheitspunkte, die jede Praxis kennen muss. 
              Inklusive echter Schadensfälle, DSGVO-Risiken und Präventionsmaßnahmen.
            </p>

            {/* Trust-Signale - RESPONSIVE GRID */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto mb-6">
              <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-foreground/60">
                <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                <span>100% Kostenlos</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-foreground/60">
                <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                <span>Keine Anmeldung</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-foreground/60">
                <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                <span>Sofort-Download</span>
              </div>
            </div>

            {/* CTA Button - OUTLINE Design */}
            <div className="text-center">
              <Button variant="outline" size="lg" asChild className="gap-2 text-sm sm:text-base">
                <Link to="/cyber-checkliste">
                  Jetzt kostenlos herunterladen
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Cards - Component */}
      <Suspense fallback={<div className="h-96 bg-muted/5 rounded-lg animate-pulse" />}>
        <CoverageCards />
      </Suspense>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Umfassender Leistungsschutz
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
              Ihre Cyberversicherung deckt alle wichtigen Bereiche ab
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-border/50 hover:border-accent/50 transition-colors">
                <CardHeader>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Weitere Anbieter - Tarife */}
      <section className="py-20 px-4 rounded-none bg-neutral-900">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-3 mb-3">
            <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-accent flex-shrink-0" />
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground text-center lg:text-left">
          TOP Cyberversicherungen<br />Tarife im Vergleich 2025
        </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-12">
            Etablierte Versicherer mit soliden Cyberversicherungs-Angeboten für Ihre Praxis
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
            {/* Markel */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">Markel</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base mb-3">Spezialist für Cyberversicherung</CardDescription>
                
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/markel">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* HDI */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">HDI</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base">Etablierter Schutz </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/hdi">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Hiscox */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">Hiscox</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  Premium-Lösung für Ärzte und Kliniken
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/hiscox">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* VHV */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">VHV</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base">Mehrfach ausgezeichneter Tarif</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/vhv">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Allianz */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">Allianz</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  Umfassender Schutz vom Marktführer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/allianz">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* AXA */}
            <Card className="will-change-transform hover:scale-[1.02] transition-transform duration-150 border-border/50 hover:border-accent/50">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl sm:text-2xl">AXA</CardTitle>
                  <Badge className="text-xs sm:text-sm bg-accent/10 text-accent border-accent/20 whitespace-nowrap pointer-events-none">
                    Gut
                  </Badge>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  Internationaler Schutz für Praxen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/tarifwissen/axa">
                  <Button variant="outline" className="w-full text-sm sm:text-base">
                    Tarif ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Alle Tarife vergleichen Link */}
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="gap-2">
              <PrefetchLink to="/tarifwissen">
                Alle 16 Tarife im Detail vergleichen
                <ArrowRight className="w-4 h-4" />
              </PrefetchLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Practice Types - Kleine Placeholders */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 text-foreground">
          Cyberversicherung für Ihre Praxis<br />Maßgeschneiderter Schutz
        </h2>
          <p className="text-xs sm:text-sm md:text-base text-center text-foreground/60 mb-16">
            Spezialisierte Tarife für alle medizinischen Einrichtungen
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {practiceTypes.map((type, idx) => (
              <PrefetchLink key={idx} to={type.link} className="block group text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-accent/5 rounded-lg flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:bg-accent/10 transition-all duration-300">
                  <type.icon className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-accent/40 group-hover:text-accent/80 group-hover:scale-110 transition-all duration-300" />
                </div>
                <p className="text-xs sm:text-sm md:text-base lg:text-lg font-medium text-foreground group-hover:text-accent transition-colors">{type.title}</p>
              </PrefetchLink>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline - So funktioniert's */}
      <section className="py-16 sm:py-20 lg:py-24 px-4">
        <div className="container mx-auto max-w-3xl">
        <h2 className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 text-foreground">
          So finden Sie die beste<br />Cyberversicherung in 3 Schritten
        </h2>
          <p className="text-xs xs:text-sm md:text-base text-center text-foreground/60 mb-8">
            Vom unverbindlichen Vergleich bis zum vollständigen Versicherungsschutz
          </p>
          
          <div className="flex justify-center mb-16">
            <Link to="/cyberversicherung/vergleichsrechner">
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-sm sm:text-base 
                  bg-transparent 
                  border border-white/20 
                  text-white 
                  hover:bg-transparent
                  hover:text-white
                  hover:border-accent 
                  hover:shadow-[0_0_15px_rgba(139,195,74,0.4)]
                  transition-all duration-150">
                Zum Vergleichsrechner
              </Button>
            </Link>
          </div>
          
          <div className="space-y-12 relative">
            
            {steps.map((step, idx) => <div key={idx} className="flex flex-col items-center gap-4 relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-lg sm:text-xl md:text-2xl font-bold flex-shrink-0 shadow-lg z-10">
                  {step.number}
                </div>
                <div className="flex-1 text-center">
                  <div className="flex flex-col items-center gap-2 mb-2">
                    <h3 className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-xs xs:text-sm md:text-base text-foreground/70">{step.description}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ - Top 3 */}
      <section className="py-20 px-4 bg-neutral-900">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 text-foreground">
            Häufig gestellte Fragen
          </h2>
          <p className="text-xs xs:text-sm md:text-base text-center text-foreground/60 mb-16">
            Die wichtigsten Fragen auf einen Blick
          </p>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => <AccordionItem key={idx} value={`item-${idx}`} className="border rounded-lg px-4 xs:px-5 sm:px-6 bg-card/30 backdrop-blur-sm">
                <AccordionTrigger className="text-left text-sm xs:text-base sm:text-lg md:text-xl font-bold text-foreground hover:no-underline py-4 xs:py-5 sm:py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-xs xs:text-sm md:text-base text-foreground/70 pb-4 xs:pb-5 sm:pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
          
          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="gap-2 w-full sm:w-auto text-xs sm:text-sm md:text-base">
              <Link to="/faq">
                Zur FAQ
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="h-96 bg-muted/5 rounded-lg animate-pulse" />}>
        <ExpertSection />
      </Suspense>

      <Suspense fallback={<div className="h-96 bg-muted/5 rounded-lg animate-pulse" />}>
        <CTASection />
      </Suspense>
      
      </main>
      
      <Footer className="bg-neutral-950" />
      </div>
    </div>;
};
export default Home;